import config from "@shared/config/client";
import queryString from "query-string";
import {
  Collection,
  CollectionsForArtistQuery,
  CreateCollection,
  PopularCollection,
} from "./types";
import { ICallableRequestBuilder } from "../requestBuilder/types";
import { DEFAULT_PAGE_SIZE } from "@/shared/constants";

const getPopularCollections =
  (request: ICallableRequestBuilder<PopularCollection[]>) => () => {
    const url = `${config.apiUrl}/popular/collections?include=true`;

    return request.call(url);
  };

const getCollections =
  (request: ICallableRequestBuilder<Collection[]>) =>
  (
    count: number = DEFAULT_PAGE_SIZE,
    skip = 0,
    searchTerm: string = "",
    marketplaces?: string[],
  ) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/collections`,
      query: {
        take: count,
        skip: skip,
        marketplaces: marketplaces,
        searchTerm,
      },
    });

    return request.call(url);
  };

const getCollectionsByAddress =
  (request: ICallableRequestBuilder<Collection[]>) => (addresses: string[]) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/collections/multiple-by-id`,
      query: {
        addresses,
      },
    });

    return request.call(url);
  };

const getCollectionsForArtist =
  (request: ICallableRequestBuilder<Collection[]>) =>
  ({ artistId, page, pageSize }: CollectionsForArtistQuery) => {
    let query = {};

    if (pageSize) {
      query = { ...query, take: pageSize };
    }

    if (pageSize && page) {
      query = { ...query, skip: page * pageSize };
    }

    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/collections/created/${artistId}`,
      query,
    });

    return request.call(url);
  };

const getCollectionsForMint =
  (request: ICallableRequestBuilder<Collection[]>) => (artistId: string) => {
    return request.call(
      `${config.apiUrl}/collections/mint/${artistId}`,
      (init) => ({
        ...init,
        method: "GET",
      }),
    );
  };

const getCollectionBySlug =
  (request: ICallableRequestBuilder<Collection>) => (slug: string) => {
    const url = queryString.stringifyUrl({
      url: `${config.apiUrl}/collections/slug/${slug}`,
    });

    return request.call(url);
  };

const getSingleCollection =
  (request: ICallableRequestBuilder<Collection>) => (id: string) => {
    const url = `${config.apiUrl}/collections/${id}`;

    return request.call(url);
  };

const updateCollection =
  (request: ICallableRequestBuilder<Collection>) =>
  (collection: CreateCollection) => {
    return request.call(`${config.apiUrl}/collections`, (init) => ({
      ...init,
      method: "PUT",
      credentials: "include",
      headers: {
        ...init.headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(collection),
    }));
  };

const requests = {
  getPopularCollections,
  getCollections,
  getCollectionsByAddress,
  getCollectionsForArtist,
  getCollectionBySlug,
  getSingleCollection,
  getCollectionsForMint,
  updateCollection,
};

export default requests;
